import { SegmentedControl, Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { useListBrands } from "../../../api/generated/endpoints/teams/teams";
import type { GenerationType, Job } from "../../../api/generated/model";
import { accessScopeQuery } from "../../../api/session";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../../platform/jobs/detail/GenerationDetailModal";
import { GENERATIONS_PAGE, useJobList, type JobFilter } from "../../../platform/jobs/job-list";
import { modelTitle } from "../../../platform/jobs/model-name";
import { useStreamStatus } from "../../../platform/stream-status";
import { formatDate } from "../../../ui/format";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import home from "../home2.module.css";
import { ResultCard } from "../ResultCard";
import { SoonControl } from "../Soon";
import classes from "./generations.module.css";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/home2/generations";
const GENERATIONS = "/teams/$teamId/brands/$brandId/home2/generations";

export type Kind = "image" | "ad-static" | "video" | "audio";

/** The Sounds page's kinds, as the Voice and sound page reads them. */
const SOUND_TYPES: readonly GenerationType[] = ["audio.sfx", "audio.tts", "audio.music"];

/**
 * The prototype's chips, in its order: each the read an existing page makes (decision 27), or
 * `Soon` where the API has no filter for it.
 */
const KINDS: readonly (
  | { label: string; kind: Kind | undefined; filter: Pick<JobFilter, "medium" | "types"> }
  | { label: string; soon: true }
)[] = [
  { label: "All", kind: undefined, filter: {} },
  { label: "Images", kind: "image", filter: { medium: "image" } },
  { label: "Ad statics", kind: "ad-static", filter: { types: ["image.advanced"] } },
  { label: "Videos", kind: "video", filter: { medium: "video" } },
  // The multi-shot storyboard is not built (G16, #981).
  { label: "Storyboard ads", soon: true },
  { label: "Audio", kind: "audio", filter: { types: SOUND_TYPES } },
  // The list filters by `active` or `all` alone: no failed-only read yet.
  { label: "Failed", soon: true },
];

type Who = "mine" | "team";

/** The jobs in days, newest first, each day's in the list's order. */
function byDay(jobs: readonly Job[]): { day: string; jobs: Job[] }[] {
  const days: { day: string; jobs: Job[] }[] = [];
  for (const job of jobs) {
    const day = formatDate(job.created_at);
    const last = days.at(-1);
    if (last?.day === day) last.jobs.push(job);
    else days.push({ day, jobs: [job] });
  }
  return days;
}

/**
 * The prototype's `#generations`: everything the brand made, the user's or the team's, in days;
 * a result opens the Studio's own detail (spec ui-perf/pro-studio-redesign, PR d1).
 */
export function GenerationsPage() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // The brand menu keeps the page mounted: another brand starts anew, with no detail of the last
  // brand's open and Mine again.
  return <BrandGenerations key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />;
}

/** One brand's results, Mine or Team, and the detail of the one opened. */
function BrandGenerations({ teamId, brandId }: { teamId: string; brandId: string }) {
  const { kind, job: linked } = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/home2/generations" });
  const chosen = KINDS.find((each) => !("soon" in each) && each.kind === kind);
  const filter = chosen !== undefined && "filter" in chosen ? chosen.filter : {};
  const [who, setWho] = useState<Who>("mine");
  // A link's job (Simple's Open in Pro) opens over the page; closing it drops the link's param.
  const [open, setOpen] = useState<string | null>(linked ?? null);
  const close = () => {
    setOpen(null);
    if (linked !== undefined) void navigate({ search: { kind }, replace: true });
  };
  const userId = useQuery({ ...accessScopeQuery(), select: (scope) => scope.user_id }).data;
  const brand = useListBrands(teamId).data?.find((each) => each.id === brandId);
  // A chip's own read: the Studio's Image or Video My Generations, or its types, 5 a page.
  const jobs = useJobList(
    teamId,
    { status: "all", brandId, ...filter, pageSize: GENERATIONS_PAGE },
    useStreamStatus(),
  );
  const catalog = useGetCatalog().data;
  const all = jobs.data?.pages.flatMap((each) => each.items);
  const shown = all?.filter((job) => who === "team" || job.created_by === userId);
  const readAgain = () => void jobs.refetch();
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Generations</h1>
          <p className={page.lede}>
            Everything you and your team made for {brand?.name ?? "this brand"}. Results save here
            on their own.
          </p>
        </div>
      </div>
      <div className={classes.toolbar}>
        <SegmentedControl
          aria-label="Whose results"
          radius="xl"
          size="xs"
          classNames={{ root: create.seg, label: create.segLabel }}
          value={who}
          onChange={(value) => setWho(value === "team" ? "team" : "mine")}
          data={[
            { value: "mine", label: "Mine" },
            { value: "team", label: "Team" },
          ]}
        />
        <nav className={classes.kinds} aria-label="Kinds">
          {KINDS.map((each) =>
            "soon" in each ? (
              <SoonControl key={each.label} className={classes.kind}>
                {each.label}
              </SoonControl>
            ) : (
              <Link
                key={each.label}
                to={GENERATIONS}
                params={{ teamId, brandId }}
                search={{ kind: each.kind }}
                className={classes.kind}
                aria-current={each.kind === kind ? "page" : undefined}
                activeOptions={{ exact: true, includeSearch: true }}
              >
                {each.label}
              </Link>
            ),
          )}
        </nav>
      </div>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {shown === undefined ? (
        <div className={`${create.gallery} ${classes.results}`}>
          {[0, 1, 2, 3].map((each) => (
            <Skeleton key={each} className={home.mediaSkeleton} />
          ))}
        </div>
      ) : shown.length === 0 && !(who === "mine" && jobs.hasNextPage) ? (
        <div className={create.empty}>
          <b>No results match</b>
          <span className={home.small}>Try another kind, or Team for everyone&apos;s.</span>
        </div>
      ) : (
        byDay(shown).map(({ day, jobs: dayJobs }) => (
          <section key={day} aria-label={day}>
            <h2 className={classes.day}>{day}</h2>
            <div className={`${create.gallery} ${classes.results}`}>
              {dayJobs.map((job) => (
                <ResultCard
                  key={job.id}
                  job={job}
                  model={modelTitle(catalog, job)}
                  onLost={readAgain}
                  onOpen={() => setOpen(job.id)}
                />
              ))}
            </div>
          </section>
        ))
      )}
      {who === "team" ? (
        // Live's: the next page as the end shows, no button.
        <LoadAhead more={morePages(jobs)} loaded={all?.length ?? 0} label="Loading more results" />
      ) : jobs.hasNextPage ? (
        // Mine keeps the user's own of the pages read: a button, so a team's many pages are
        // read only when asked for.
        <div className={classes.more}>
          {shown?.length === 0 ? <b>None of yours in the latest results</b> : null}
          <button
            type="button"
            className={home.smallButton}
            disabled={jobs.isFetchingNextPage}
            onClick={() => void jobs.fetchNextPage()}
          >
            Show more
          </button>
        </div>
      ) : null}
      {open === null ? null : (
        <GenerationDetailModal teamId={teamId} jobId={open} onClose={close} />
      )}
    </div>
  );
}
